import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { hydrateStockIssueCategoryFilterLabels } from '#/api/system/stockIssueCategory/fetchSelectOptions';
import useSideDetail from '#/hooks/useSideDetail';
import { createAsyncFilterLabelCache } from '#/modules/commonComponents/table/asyncFilterLabelCache';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';
import StockIssueSideMsg from '#/modules/imsMgmt/receipts/stockIssue/components/StockIssueSideMsg';
import { buildStockIssueColumns } from '#/modules/imsMgmt/receipts/stockIssue/data/stockIssueColumns';
import { getStockIssueApiErrorMessage } from '#/modules/imsMgmt/receipts/stockIssue/helper/apiError';
import { applyIssueDateRangeToFilters } from '#/modules/imsMgmt/receipts/stockIssue/helper/issueDateListFilter';
import useStockIssueList from '#/modules/imsMgmt/receipts/stockIssue/hooks/useStockIssueList';

const STOCK_ISSUE_SORT_FIELD_LABELS = {
  ...DEFAULT_LIST_SORT_FIELD_LABELS,
  issueNo: '出庫單號',
  issueDate: '出庫日',
  status: '狀態',
};

const StockIssueListPage = () => {
  const listRef = useRef(null);
  const [categoryLabelCache] = useState(createAsyncFilterLabelCache);
  const [labelCacheVersion, setLabelCacheVersion] = useState(0);
  const { displayData, setSelectedData } = useSideDetail();
  const canCreate = useCan(PERM.STOCK_ISSUE_CREATE);
  const canUpdate = useCan(PERM.STOCK_ISSUE_UPDATE);

  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetListQuery,
    setListQuery,
  } = useStockIssueList();

  useEffect(() => {
    let cancelled = false;

    hydrateStockIssueCategoryFilterLabels(
      listQuery.filters?.stockIssueCategoryId,
      categoryLabelCache,
    ).then((didHydrate) => {
      if (!cancelled && didHydrate) {
        setLabelCacheVersion((version) => version + 1);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [listQuery.filters?.stockIssueCategoryId, categoryLabelCache]);

  const setIssueDateRangeFilter = useCallback((range) => {
    setListQuery((prev) => ({
      ...prev,
      page: 1,
      filters: applyIssueDateRangeToFilters(prev.filters, range),
    }));
  }, [setListQuery]);

  const handleFilterChange = useCallback((key, value) => {
    if (key === 'issueDateFrom' && value === undefined) {
      setIssueDateRangeFilter(undefined);
      return;
    }
    setFilter(key, value);
  }, [setFilter, setIssueDateRangeFilter]);

  const columns = useMemo(
    () => {
      void labelCacheVersion;
      return buildStockIssueColumns({
        filters: listQuery.filters || {},
        onFilterChange: handleFilterChange,
        onIssueDateRangeChange: setIssueDateRangeFilter,
        categoryLabelCache,
        canShowEdit: (row) => canUpdate && row.status === 'DRAFT',
      });
    },
    [
      listQuery.filters,
      handleFilterChange,
      setIssueDateRangeFilter,
      categoryLabelCache,
      labelCacheVersion,
      canUpdate,
    ],
  );

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns, { filters: listQuery.filters }),
    [columns, listQuery.filters],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const handleRowClick = useCallback((row, isSelected) => {
    if (isSelected) {
      setSelectedData(row.original);
      return;
    }

    setSelectedData(null);
  }, [setSelectedData]);

  const headerAction = canCreate ? (
    <ListAddButton to={ROUTES.stockIssueCreate} />
  ) : null;

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <h1 className="page_title shrink-0">
          <span className="border-l-5 border-primary-400 pl-2.5">出庫管理</span>
        </h1>
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <ListTablePanel
              ref={listRef}
              tableKey="ims.stock_issue.list"
              columns={columns}
              rows={rows}
              pagination={pagination}
              isLoading={isLoading}
              isError={isError}
              errorMessage={isError ? getStockIssueApiErrorMessage(error, '載入列表失敗') : undefined}
              onRetry={isError ? () => { refetch(); } : undefined}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onRowClick={handleRowClick}
              onSearch={setKeyword}
              onReset={resetListQuery}
              initialKeyword={listQuery.keyword}
              searchPlaceholder="搜尋單號、備註、出庫類別或庫存編號"
              headerAction={headerAction}
              listQuery={listQuery}
              filterStatusFields={filterStatusFields}
              sortFieldLabels={STOCK_ISSUE_SORT_FIELD_LABELS}
              onFilterChange={handleFilterChange}
              onKeywordClear={() => setKeyword('')}
              onSortRemove={removeSortAt}
              onSortingChange={handleSortingChange}
            />
          </div>

          <StockIssueSideMsg
            displayData={displayData}
            setSelectedData={setSelectedData}
          />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default StockIssueListPage;
